// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	buildGoogleFaviconUrl,
	TAB_DISGUISE_MODE_CLASSROOM,
	TAB_DISGUISE_MODE_CUSTOM,
	TAB_DISGUISE_MODE_DRIVE,
	TAB_DISGUISE_MODE_NONE,
	TAB_DISGUISE_PRESETS,
} from '@app/features/tab_disguise/state/TabDisguise';
import * as TabDisguiseCommands from '@app/features/tab_disguise/commands/TabDisguiseCommands';
import {Combobox} from '@app/features/ui/components/form/FormCombobox';
import {Input} from '@app/features/ui/components/form/FormInput';
import styles from '@app/features/user/components/modals/tabs/appearance_tab/TabDisguiseSection.module.css';
import UserSettingsState from '@app/features/user/state/UserSettings';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useState} from 'react';

const TAB_DISGUISE_DESCRIPTOR = msg({
	message: 'Tab disguise',
	comment: 'Short label for the hidden-tab disguise settings section. Keep it concise.',
});
const DISGUISE_THE_TAB_TITLE_AND_FAVICON_WHEN_THIS_TAB_DESCRIPTOR = msg({
	message: 'Disguise the tab title and favicon when this tab is hidden or inactive.',
	comment: 'Description for the hidden-tab disguise settings section.',
});
const DISGUISE_STYLE_DESCRIPTOR = msg({
	message: 'Disguise style',
	comment: 'Label for the hidden-tab disguise style picker. Keep it concise.',
});
const NO_DISGUISE_DESCRIPTOR = msg({
	message: 'Off',
	comment: 'Label for disabling the hidden-tab disguise. Keep it concise.',
});
const GOOGLE_DRIVE_DESCRIPTOR = msg({
	message: 'Google Drive',
	comment: 'Label for the Google Drive hidden-tab disguise preset. Keep it concise.',
});
const GOOGLE_CLASSROOM_DESCRIPTOR = msg({
	message: 'Google Classroom',
	comment: 'Label for the Google Classroom hidden-tab disguise preset. Keep it concise.',
});
const CUSTOM_DESCRIPTOR = msg({
	message: 'Custom',
	comment: 'Label for the custom hidden-tab disguise option. Keep it concise.',
});
const CUSTOM_TITLE_DESCRIPTOR = msg({
	message: 'Custom title',
	comment: 'Label for the custom hidden-tab title input. Keep it concise.',
});
const CUSTOM_TITLE_PLACEHOLDER_DESCRIPTOR = msg({
	message: 'Title shown when the tab is hidden',
	comment: 'Placeholder for the custom hidden-tab title input.',
});
const FAVICON_DOMAIN_DESCRIPTOR = msg({
	message: 'Favicon domain',
	comment: 'Label for the custom hidden-tab favicon domain input. Keep it concise.',
});
const FAVICON_DOMAIN_PLACEHOLDER_DESCRIPTOR = msg({
	message: 'example.com',
	comment: 'Placeholder for the custom hidden-tab favicon domain input.',
});
const THE_FAVICON_IS_FETCHED_FROM_GOOGLE_S_FAVICON_DESCRIPTOR = msg({
	message: "The favicon is fetched from Google's favicon service for the domain you enter.",
	comment: 'Description for the custom hidden-tab favicon domain input.',
});

function extractDomainFromGoogleFaviconUrl(favicon: string | null): string {
	if (!favicon) {
		return '';
	}
	try {
		const url = new URL(favicon);
		if (url.hostname !== 'www.google.com' || !url.pathname.startsWith('/s2/favicons')) {
			return '';
		}
		return url.searchParams.get('domain_url') ?? '';
	} catch {
		return '';
	}
}

export const TAB_DISGUISE_SECTION_ID = 'tab-disguise';

export const TabDisguiseSection: React.FC = observer(() => {
	const {i18n} = useLingui();
	const mode = UserSettingsState.tabDisguiseMode;
	const storedTitle = UserSettingsState.tabDisguiseTitle;
	const storedFavicon = UserSettingsState.tabDisguiseFavicon;
	const [draftTitle, setDraftTitle] = useState(storedTitle ?? '');
	const [draftDomain, setDraftDomain] = useState(() => extractDomainFromGoogleFaviconUrl(storedFavicon));
	useEffect(() => {
		setDraftTitle(storedTitle ?? '');
		setDraftDomain(extractDomainFromGoogleFaviconUrl(storedFavicon));
	}, [storedTitle, storedFavicon, mode]);
	const handleModeChange = useCallback((value: string) => {
		void TabDisguiseCommands.setTabDisguiseMode(value);
	}, []);
	const saveCustom = useCallback(() => {
		if (UserSettingsState.tabDisguiseMode !== TAB_DISGUISE_MODE_CUSTOM) {
			return;
		}
		void TabDisguiseCommands.setCustomTabDisguise({title: draftTitle, domain: draftDomain});
	}, [draftTitle, draftDomain]);
	const previewFavicon =
		mode === TAB_DISGUISE_MODE_DRIVE || mode === TAB_DISGUISE_MODE_CLASSROOM
			? (TAB_DISGUISE_PRESETS[mode]?.favicon ?? '')
			: draftDomain.trim()
				? buildGoogleFaviconUrl(draftDomain)
				: (storedFavicon ?? '');
	const previewTitle =
		mode === TAB_DISGUISE_MODE_DRIVE || mode === TAB_DISGUISE_MODE_CLASSROOM
			? (TAB_DISGUISE_PRESETS[mode]?.title ?? '')
			: draftTitle.trim() || storedTitle || '';
	return (
		<div className={styles.container} data-flx="user.appearance-tab.tab-disguise-section.container">
			<Combobox
				label={i18n._(DISGUISE_STYLE_DESCRIPTOR)}
				description={i18n._(DISGUISE_THE_TAB_TITLE_AND_FAVICON_WHEN_THIS_TAB_DESCRIPTOR)}
				value={mode}
				options={[
					{value: TAB_DISGUISE_MODE_NONE, label: i18n._(NO_DISGUISE_DESCRIPTOR)},
					{value: TAB_DISGUISE_MODE_DRIVE, label: i18n._(GOOGLE_DRIVE_DESCRIPTOR)},
					{value: TAB_DISGUISE_MODE_CLASSROOM, label: i18n._(GOOGLE_CLASSROOM_DESCRIPTOR)},
					{value: TAB_DISGUISE_MODE_CUSTOM, label: i18n._(CUSTOM_DESCRIPTOR)},
				]}
				onChange={handleModeChange}
				data-flx="user.appearance-tab.tab-disguise-section.combobox.set-mode"
			/>
			{mode === TAB_DISGUISE_MODE_CUSTOM && (
				<div className={styles.customFields} data-flx="user.appearance-tab.tab-disguise-section.custom-fields">
					<Input
						label={i18n._(CUSTOM_TITLE_DESCRIPTOR)}
						placeholder={i18n._(CUSTOM_TITLE_PLACEHOLDER_DESCRIPTOR)}
						maxLength={140}
						value={draftTitle}
						onChange={(event) => setDraftTitle(event.target.value)}
						onBlur={saveCustom}
						onKeyDown={(event) => {
							if (event.key === 'Enter') {
								saveCustom();
							}
						}}
						data-flx="user.appearance-tab.tab-disguise-section.input.title"
					/>
					<Input
						label={i18n._(FAVICON_DOMAIN_DESCRIPTOR)}
						footer={<div>{i18n._(THE_FAVICON_IS_FETCHED_FROM_GOOGLE_S_FAVICON_DESCRIPTOR)}</div>}
						placeholder={i18n._(FAVICON_DOMAIN_PLACEHOLDER_DESCRIPTOR)}
						maxLength={253}
						value={draftDomain}
						onChange={(event) => setDraftDomain(event.target.value)}
						onBlur={saveCustom}
						onKeyDown={(event) => {
							if (event.key === 'Enter') {
								saveCustom();
							}
						}}
						data-flx="user.appearance-tab.tab-disguise-section.input.domain"
					/>
				</div>
			)}
			{mode !== TAB_DISGUISE_MODE_NONE && (previewTitle || previewFavicon) && (
				<div className={styles.previewRow} data-flx="user.appearance-tab.tab-disguise-section.preview-row">
					{previewFavicon && (
						<img
							className={styles.previewIcon}
							src={previewFavicon}
							alt=""
							data-flx="user.appearance-tab.tab-disguise-section.preview-icon"
						/>
					)}
					{previewTitle && (
						<span
							className={styles.previewTitle}
							data-flx="user.appearance-tab.tab-disguise-section.preview-title"
						>
							{previewTitle}
						</span>
					)}
				</div>
			)}
		</div>
	);
});

export const TabDisguiseSectionTitle = TAB_DISGUISE_DESCRIPTOR;
